import {
    featureDisplayState,
    type FeatureResultState,
    type ProjectedFeature,
} from './project-features.js';

/**
 * Findings: which sub-check the analyzer failed, and on how many creatives.
 *
 * Criterion names say *which* rule missed; a criterion is a bundle of sub-checks, and the
 * sub-check is the production note — "Max words per frame" is something a producer can act on,
 * "Focus on 1 Main Message scored 0.4" is not. Today a sub-check is reachable only one creative
 * at a time, inside an expanded breakdown row.
 *
 * Grouping is by sub-check *name*, never by its `detail`: the detail carries this creative's
 * measurement ("Max words per frame: 36"), so grouping on it would split one recurring problem
 * into a row per value, each occurring once. The measurements are kept as examples instead.
 *
 * Only sub-checks DS marked failed count. A passing sub-check inside a failing criterion is
 * context, not the reason it failed, and counting it would make the ranking meaningless.
 */

/** Findings kept by default — the list is evidence, not an exhaustive log. */
export const MAX_FINDINGS = 12;

/** Measurements shown per finding before the rest are summarised as a remainder. */
export const MAX_FINDING_EXAMPLES = 3;

/** Display states whose sub-checks describe a problem. */
const REPORTED_STATES: ReadonlySet<FeatureResultState> = new Set(['FAIL', 'PARTIAL']);

export type FindingFeatureInput = Pick<
    ProjectedFeature,
    'code' | 'name' | 'definition' | 'displayOrder' | 'state' | 'featureScore' | 'subChecks'
>;

/** One failing sub-check and how widely it failed. */
export interface CriterionFinding {
    /** Stable identity: the criterion it belongs to plus the sub-check name. */
    key: string;
    criterionCode: string | null;
    criterionName: string;
    criterionDefinition: string | null;
    /** The rule DS evaluated — the same text however many creatives failed it. */
    subCheck: string;
    /**
     * What the analyzer measured, distinct and in first-seen order, capped at
     * {@link MAX_FINDING_EXAMPLES}. Empty when the sub-check reports no readout. A readout that
     * restates the rule is trimmed to the measurement — "Max words per frame: 36" becomes "36".
     */
    examples: string[];
    /** Distinct measurements beyond the examples shown. */
    otherExampleCount: number;
    /** Creatives where this sub-check failed. */
    assetCount: number;
    /** Creatives where the carrying criterion outright failed. */
    failedCount: number;
    /** Creatives where the carrying criterion partially passed. */
    partialCount: number;
}

interface FindingAccumulator extends Omit<CriterionFinding, 'examples' | 'otherExampleCount'> {
    displayOrder: number | null;
    /** Distinct measurements in first-seen order; trimmed to the cap on the way out. */
    measurements: Set<string>;
}

function findingKey(criterionKey: string, subCheckName: string): string {
    return `${criterionKey}::${subCheckName}`;
}

/**
 * The measured part of a readout. DS commonly repeats the rule in it ("Max words per frame: 36"),
 * which reads as a stutter next to the rule's own name, so a leading restatement is dropped.
 * Exports keep DS's wording verbatim; this trimming is for the aggregated view only.
 */
function measurementOf(subCheckName: string, detail: string): string {
    const prefix = `${subCheckName.toLowerCase()}:`;
    if (!detail.toLowerCase().startsWith(prefix)) return detail;

    const measured = detail.slice(prefix.length).trim();
    return measured.length > 0 ? measured : detail;
}

/**
 * Aggregate failing sub-checks across any set of scored creatives, most widespread first. A
 * creative counts once per sub-check name even if DS repeats it.
 */
export function aggregateFindings(
    featuresPerAsset: readonly (readonly FindingFeatureInput[])[],
    limit: number = MAX_FINDINGS,
): CriterionFinding[] {
    const byFinding = new Map<string, FindingAccumulator>();

    for (const features of featuresPerAsset) {
        // Per creative, so a sub-check DS reports twice does not inflate the count.
        const seen = new Set<string>();

        for (const feature of features) {
            const state = featureDisplayState(feature);
            if (!REPORTED_STATES.has(state)) continue;

            const criterionKey = feature.code ?? feature.name;
            for (const sub of feature.subChecks) {
                if (sub.passed !== false) continue;

                const name = sub.name.trim();
                if (name.length === 0) continue;

                const key = findingKey(criterionKey, name);
                const entry =
                    byFinding.get(key) ??
                    ({
                        key,
                        criterionCode: feature.code,
                        criterionName: feature.name,
                        criterionDefinition: feature.definition,
                        subCheck: name,
                        assetCount: 0,
                        failedCount: 0,
                        partialCount: 0,
                        displayOrder: feature.displayOrder,
                        measurements: new Set<string>(),
                    } satisfies FindingAccumulator);

                if (!entry.criterionDefinition && feature.definition) {
                    entry.criterionDefinition = feature.definition;
                }

                const readout = sub.detail?.trim();
                if (readout) entry.measurements.add(measurementOf(name, readout));

                if (!seen.has(key)) {
                    seen.add(key);
                    entry.assetCount += 1;
                    if (state === 'FAIL') entry.failedCount += 1;
                    else entry.partialCount += 1;
                }
                byFinding.set(key, entry);
            }
        }
    }

    return [...byFinding.values()]
        .sort(
            (a, b) =>
                b.assetCount - a.assetCount ||
                (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
                    (b.displayOrder ?? Number.MAX_SAFE_INTEGER) ||
                a.subCheck.localeCompare(b.subCheck),
        )
        .slice(0, limit)
        .map(({ displayOrder: _displayOrder, measurements, ...finding }) => ({
            ...finding,
            examples: [...measurements].slice(0, MAX_FINDING_EXAMPLES),
            otherExampleCount: Math.max(0, measurements.size - MAX_FINDING_EXAMPLES),
        }));
}
